import React, { useState, useEffect } from 'react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Bell, 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle,
  Clock,
  Calendar,
  AlertTriangle,
  Search,
  AlarmClock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import api from '../lib/api';

const REMINDER_TYPES = [
  { value: 'deadline', label: 'Deadline' },
  { value: 'court_date', label: 'Court Date' },
  { value: 'invoice_due', label: 'Invoice Due' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'custom', label: 'Custom' },
];

export const Reminders = () => {
  const [reminders, setReminders] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('upcoming');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [snoozeId, setSnoozeId] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    reminder_type: 'custom',
    case_id: '',
    client_id: '',
    notify_via: ['in_app'],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [remindersRes, upcomingRes, casesRes, clientsRes] = await Promise.all([
        api.get('/reminders'),
        api.get('/reminders/upcoming', { params: { days: 14 } }),
        api.get('/cases'),
        api.get('/clients'),
      ]);
      setReminders(remindersRes.data);
      setUpcomingReminders(upcomingRes.data);
      setCases(casesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        due_date: new Date(formData.due_date).toISOString(),
      };
      if (!data.case_id || data.case_id === 'none') delete data.case_id;
      if (!data.client_id || data.client_id === 'none') delete data.client_id;
      
      if (editingReminder) {
        await api.put(`/reminders/${editingReminder.reminder_id}`, data);
        toast.success('Reminder updated');
      } else {
        await api.post('/reminders', data);
        toast.success('Reminder created');
      }
      fetchData();
      resetForm();
    } catch (error) {
      toast.error('Failed to save reminder');
    }
  };

  const handleComplete = async (reminderId) => {
    try {
      await api.put(`/reminders/${reminderId}/complete`);
      toast.success('Reminder completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };

  const handleSnooze = async (hours) => {
    if (!snoozeId) return;
    try {
      await api.put(`/reminders/${snoozeId}/snooze`, { hours });
      toast.success(`Snoozed for ${hours} hours`);
      fetchData();
    } catch (error) {
      toast.error('Failed to snooze reminder');
    } finally {
      setSnoozeId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/reminders/${deleteId}`);
      toast.success('Reminder deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete reminder');
    } finally {
      setDeleteId(null);
    }
  };

  const handleEdit = (reminder) => {
    setEditingReminder(reminder);
    setFormData({
      title: reminder.title,
      description: reminder.description || '',
      due_date: reminder.due_date?.split('T')[0] || '',
      reminder_type: reminder.reminder_type,
      case_id: reminder.case_id || '',
      client_id: reminder.client_id || '',
      notify_via: reminder.notify_via || ['in_app'],
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      due_date: '',
      reminder_type: 'custom',
      case_id: '',
      client_id: '',
      notify_via: ['in_app'],
    });
    setEditingReminder(null);
    setIsDialogOpen(false);
  };

  const getTypeColor = (type) => {
    const colors = {
      deadline: 'bg-red-100 text-red-700',
      court_date: 'bg-purple-100 text-purple-700',
      invoice_due: 'bg-green-100 text-green-700',
      follow_up: 'bg-blue-100 text-blue-700',
      custom: 'bg-slate-100 text-slate-700',
    };
    return colors[type] || colors.custom;
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
  };

  const activeReminders = activeTab === 'upcoming' ? upcomingReminders : reminders.filter(r => r.status === 'active');
  const completedReminders = reminders.filter(r => r.status === 'completed');

  const filteredReminders = (activeTab === 'completed' ? completedReminders : activeReminders).filter(r =>
    r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (r.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <PageLoader text="Loading reminders..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reminders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Reminders & Notifications</h1>
          <p className="text-slate-500 mt-1">Stay on top of deadlines and important dates</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-reminder-btn">
              <Plus className="h-4 w-4" />
              Add Reminder
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editingReminder ? 'Edit Reminder' : 'Create Reminder'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="File motion deadline"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="due_date">Due Date *</Label>
                  <Input
                    id="due_date"
                    type="datetime-local"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reminder_type">Type</Label>
                  <Select value={formData.reminder_type} onValueChange={(v) => setFormData({ ...formData, reminder_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {REMINDER_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="case_id">Related Case</Label>
                  <Select value={formData.case_id} onValueChange={(v) => setFormData({ ...formData, case_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select case" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {cases.map(c => (
                        <SelectItem key={c.case_id} value={c.case_id}>{c.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_id">Client</Label>
                  <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit">{editingReminder ? 'Update' : 'Create'} Reminder</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Overdue</p>
                <p className="text-xl font-bold">
                  {reminders.filter(r => r.status === 'active' && isOverdue(r.due_date)).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Due This Week</p>
                <p className="text-xl font-bold">{upcomingReminders.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-xl font-bold">{completedReminders.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs and Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search reminders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="all">All Active</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Reminders List */}
      <div className="space-y-3">
        {filteredReminders.map((reminder) => (
          <Card key={reminder.reminder_id} className={`${isOverdue(reminder.due_date) && reminder.status === 'active' ? 'border-red-200 bg-red-50/50' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${isOverdue(reminder.due_date) && reminder.status === 'active' ? 'bg-red-100' : 'bg-slate-100'}`}>
                    <Bell className={`h-5 w-5 ${isOverdue(reminder.due_date) && reminder.status === 'active' ? 'text-red-600' : 'text-slate-600'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{reminder.title}</h3>
                      <Badge className={getTypeColor(reminder.reminder_type)}>
                        {REMINDER_TYPES.find(t => t.value === reminder.reminder_type)?.label}
                      </Badge>
                      {isOverdue(reminder.due_date) && reminder.status === 'active' && (
                        <Badge variant="destructive">Overdue</Badge>
                      )}
                    </div>
                    {reminder.description && (
                      <p className="text-sm text-slate-500 mt-1">{reminder.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(reminder.due_date)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {reminder.status === 'active' && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => handleComplete(reminder.reminder_id)} className="text-green-600">
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setSnoozeId(reminder.reminder_id)}>
                        <AlarmClock className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(reminder)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setDeleteId(reminder.reminder_id)} className="text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {filteredReminders.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-slate-500">
              <Bell className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p>No reminders found</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Snooze Dialog */}
      <AlertDialog open={!!snoozeId} onOpenChange={() => setSnoozeId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Snooze Reminder</AlertDialogTitle>
            <AlertDialogDescription>
              How long would you like to snooze this reminder?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-2 justify-center py-4">
            <Button variant="outline" onClick={() => handleSnooze(1)}>1 hour</Button>
            <Button variant="outline" onClick={() => handleSnooze(4)}>4 hours</Button>
            <Button variant="outline" onClick={() => handleSnooze(24)}>1 day</Button>
            <Button variant="outline" onClick={() => handleSnooze(72)}>3 days</Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Reminder?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Reminders;
